import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Checkbox,
  ListItem,
  ListItemText,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { WithGuideHelp } from '@modules/ui-components/dialogs';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { useBigNumbers } from '../hooks/useBigNumbers';
import { useTasks } from '../hooks/useTasks';
import type { BigNumber } from '@segmently/schema-models/entities';
import type { BigNumberFormData, SortDirection, MinimalTask } from '../types';

const INITIAL_FORM_DATA: BigNumberFormData = {
  description: '',
  amount: '',
  selectedTaskIds: [],
};

const BACKDROP_SX = {
  backgroundColor: 'rgba(0, 0, 0, 0.15)',
  backdropFilter: 'blur(20px)',
};

// iOS nav bar layout — close LEFT, title CENTRE, phantom RIGHT.
const DIALOG_TITLE_SX = {
  display: 'grid',
  gridTemplateColumns: '40px 1fr 40px',
  alignItems: 'center',
  gap: 1,
  padding: '0 0 8px',
};

const DIALOG_CONTENT_SX = {
  padding: '0',
  my: 2,
};

type DialogMode = 'create' | 'edit';

export default function BigNumbersTable({ projectId }: { projectId: string }) {
  const { bigNumbers, loading: _loading, error, setError, createBigNumber, updateBigNumber, deleteBigNumber } = useBigNumbers(projectId);
  const tasks = useTasks(projectId);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRow, setSelectedRow] = useState<BigNumber | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>('create');
  const [formData, setFormData] = useState<BigNumberFormData>(INITIAL_FORM_DATA);
  const [openTaskSelection, setOpenTaskSelection] = useState(false);
  const [tempSelectedTaskIds, setTempSelectedTaskIds] = useState<string[]>([]);

  const [sortField, setSortField] = useState<'description' | 'amount'>('amount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const guideService = useMemo(() => new GuideService(), []);

  // --- Bulk selection ---
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === bigNumbers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(bigNumbers.map(r => r.id).filter(Boolean)));
    }
  }, [selectedIds.size, bigNumbers]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    try {
      await Promise.all(Array.from(selectedIds).map(id => deleteBigNumber(id)));
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Error deleting selected big numbers:', err);
      setError('Failed to delete selected big numbers.');
    }
  }, [selectedIds, deleteBigNumber, setError]);

  // --- Sorting ---

  const handleSort = (field: 'description' | 'amount') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedRows = useMemo(() => {
    const s = [...bigNumbers];
    s.sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      if (sortField === 'amount') {
        av = Number(a.amount) || 0;
        bv = Number(b.amount) || 0;
      } else {
        av = (a[sortField] || '').toString().toLowerCase();
        bv = (b[sortField] || '').toString().toLowerCase();
      }
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return s;
  }, [bigNumbers, sortField, sortDirection]);

  // --- Context Menu ---

  const openMenu = (e: React.MouseEvent<HTMLElement>, row: BigNumber) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  // --- Row Click / Create / Edit / Delete ---

  const handleRowClick = (row: BigNumber) => {
    setSelectedRow(row);
    setDialogMode('edit');
    setFormData({
      description: row.description ?? '',
      amount: row.amount ?? '',
      selectedTaskIds: row.selectedTaskIds ?? [],
    });
    setOpenDialog(true);
  };

  const handleCreate = () => {
    setDialogMode('create');
    setFormData(INITIAL_FORM_DATA);
    setOpenDialog(true);
  };

  const handleEdit = () => {
    if (!selectedRow) return;
    setDialogMode('edit');
    setFormData({
      description: selectedRow.description ?? '',
      amount: selectedRow.amount ?? '',
      selectedTaskIds: selectedRow.selectedTaskIds ?? [],
    });
    setOpenDialog(true);
    closeMenu();
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      await deleteBigNumber(selectedRow.id);
      closeMenu();
    } catch (err) {
      console.error('Delete big number failed:', err);
      setError('Failed to delete big number.');
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        description: (formData.description || '').toString(),
        amount: parseFloat(formData.amount) || 0,
        selectedTaskIds: Array.isArray(formData.selectedTaskIds) ? formData.selectedTaskIds : [],
      };

      if (dialogMode === 'create') {
        await createBigNumber({
          description: payload.description,
          amount: String(payload.amount),
          selectedTaskIds: payload.selectedTaskIds,
        });
      } else if (selectedRow) {
        await updateBigNumber(selectedRow.id, {
          description: payload.description,
          amount: String(payload.amount),
          selectedTaskIds: payload.selectedTaskIds,
        });
      }

      setOpenDialog(false);
      setSelectedRow(null);
    } catch (err) {
      console.error('Save big number failed:', err);
      setError('Failed to save big number.');
    }
  };

  const handleCancel = () => {
    setOpenDialog(false);
  };

  // --- Task Selection ---

  const handleOpenTaskSelection = () => {
    setTempSelectedTaskIds([...(formData.selectedTaskIds || [])]);
    setOpenTaskSelection(true);
  };

  const handleCloseTaskSelection = () => {
    setOpenTaskSelection(false);
  };

  const handleConfirmTaskSelection = () => {
    setFormData({ ...formData, selectedTaskIds: tempSelectedTaskIds });
    setOpenTaskSelection(false);
  };

  const handleTaskToggle = (taskId: string) => {
    setTempSelectedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId],
    );
  };

  // --- Task name resolution helper ---

  const resolveTaskNames = (taskIds: string[] | undefined): { id: string; name: string }[] => {
    if (!Array.isArray(taskIds)) return [];
    return taskIds
      .map((tid) => {
        const t = tasks.find((tt: MinimalTask) => tt.id === tid);
        return t ? { id: tid, name: t.name } : null;
      })
      .filter(Boolean) as { id: string; name: string }[];
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" gutterBottom>
            Big Numbers
          </Typography>
          {selectedIds.size > 0 && (
            <>
              <Chip label={selectedIds.size} size="small" color="primary" sx={{ fontWeight: 'medium' }} />
              <Tooltip title="Delete selected">
                <IconButton onClick={handleDeleteSelected} color="error" size="small" aria-label="Delete selected">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
        <WithGuideHelp guideKey="big-numbers-create-button" guideService={guideService} position="top-right">
          <Button
            data-testid="bignumbers-create-button"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            size="small"
            disabled={!projectId}
          >
            Create Big Number
          </Button>
        </WithGuideHelp>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer>
        <Table data-testid="bignumbers-table" sx={{ minWidth: 650, tableLayout: 'auto', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={bigNumbers.length > 0 && selectedIds.size === bigNumbers.length}
                  indeterminate={selectedIds.size > 0 && selectedIds.size < bigNumbers.length}
                  onChange={handleSelectAll}
                  disabled={bigNumbers.length === 0}
                  size="small"
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', maxWidth: 500 }}>
                <TableSortLabel
                  active={sortField === 'description'}
                  direction={sortField === 'description' ? sortDirection : 'asc'}
                  onClick={() => handleSort('description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 160, whiteSpace: 'nowrap' }} align="right">
                <TableSortLabel
                  active={sortField === 'amount'}
                  direction={sortField === 'amount' ? sortDirection : 'asc'}
                  onClick={() => handleSort('amount')}
                >
                  Amount
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', maxWidth: 280 }}>Tasks</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 100 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.length > 0 ? (
              sortedRows.map((row) => {
                const taskNames = resolveTaskNames(row.selectedTaskIds);
                return (
                  <TableRow
                    key={row.id}
                    data-testid={`bignumbers-row-${row.id}`}
                    hover
                    onClick={() => handleRowClick(row)}
                    sx={{ cursor: 'pointer' }}
                    selected={selectedIds.has(row.id)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(row.id)}
                        onChange={() => handleToggleSelect(row.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 520 }}>
                      {row.description ?? ''}
                    </TableCell>
                    <TableCell align="right">{row.amount ?? ''}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      {taskNames.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {taskNames.map((t) => (
                            <Chip key={t.id} label={t.name} size="small" variant="outlined" />
                          ))}
                        </Box>
                      ) : (
                        'None'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => openMenu(e, row)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No big numbers found. Click &quot;Create Big Number&quot; to add one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Context Menu */}
      <Menu
        data-testid="bignumbers-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disablePortal
        PaperProps={{ sx: { minWidth: 160 } }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog
        data-testid="bignumbers-dialog"
        open={openDialog}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        slotProps={{ backdrop: { sx: BACKDROP_SX } }}
      >
        <DialogTitle sx={DIALOG_TITLE_SX}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <IconButton
              onClick={handleCancel}
              size="small"
              aria-label="close dialog"
              sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            component="span"
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {dialogMode === 'create' ? 'Create Big Number' : 'Edit Big Number'}
          </Box>
          <Box />
        </DialogTitle>
        <DialogContent sx={DIALOG_CONTENT_SX}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              inputProps={{ 'data-testid': 'bignumbers-field-description' }}
            />
            <TextField
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              inputProps={{ step: '0.1', 'data-testid': 'bignumbers-field-amount' }}
              fullWidth
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Tasks
              </Typography>
              <Button variant="contained" size="small" onClick={handleOpenTaskSelection}>
                Select Tasks
              </Button>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {formData.selectedTaskIds.map((tid) => {
                  const t = tasks.find((tt: MinimalTask) => tt.id === tid);
                  return (
                    <Chip
                      key={tid}
                      label={t ? t.name : tid}
                      size="small"
                      onDelete={() =>
                        setFormData((prev) => ({
                          ...prev,
                          selectedTaskIds: prev.selectedTaskIds.filter((id) => id !== tid),
                        }))
                      }
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: '16px 0 0' }}>
          <Button data-testid="bignumbers-save-button" onClick={handleSave} variant="contained">
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Selection Dialog */}
      <Dialog
        data-testid="bignumbers-task-dialog"
        open={openTaskSelection}
        onClose={handleCloseTaskSelection}
        maxWidth="sm"
        fullWidth
        slotProps={{ backdrop: { sx: BACKDROP_SX } }}
      >
        <DialogTitle sx={DIALOG_TITLE_SX}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <IconButton
              onClick={handleCloseTaskSelection}
              size="small"
              aria-label="close dialog"
              sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            component="span"
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            Select Tasks
          </Box>
          <Box />
        </DialogTitle>
        <DialogContent sx={DIALOG_CONTENT_SX}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1' }}>
            {tasks.map((task: MinimalTask) => (
              <ListItem key={task.id} dense>
                <Checkbox
                  edge="start"
                  checked={tempSelectedTaskIds.includes(task.id)}
                  onChange={() => handleTaskToggle(task.id)}
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemText primary={task.name} />
              </ListItem>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: '16px 0 0' }}>
          <Button onClick={handleConfirmTaskSelection} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
